import { useEffect, useState } from "react";
import { toast } from "sonner";
import { http, formatApiError } from "../../lib/api";
import { Modal, F, inp, btnPrimary, btnGhost, eur, today } from "./common";

export const openPdf = async (url) => {
  try {
    const r = await http.get(url, { responseType: "blob" });
    const u = URL.createObjectURL(r.data);
    window.open(u, "_blank");
    setTimeout(() => URL.revokeObjectURL(u), 60000);
  } catch (e) { toast.error(formatApiError(e)); }
};

export const OrderCreateDialog = ({ needIds, onClose, onDone }) => {
  const [sups, setSups] = useState([]);
  const [v, setV] = useState({ supplier_id: "", notes: "", delivery_address: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { http.get("/equipment/suppliers").then((r) => setSups(r.data)); }, []);
  const go = async () => {
    setBusy(true);
    try { const r = await http.post("/equipment/orders", { ...v, need_ids: needIds }); toast.success(`Bon de commande ${r.data.number} créé`); onDone(); onClose(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title="Nouveau bon de commande" onClose={onClose} testid="order-create">
      <p className="text-sm text-slate-600">{needIds.length} besoin(s) validé(s) seront regroupés dans ce bon.</p>
      <F label="Fournisseur"><select className={inp} value={v.supplier_id} onChange={(e) => setV({ ...v, supplier_id: e.target.value })} data-testid="order-supplier">
        <option value="">— Choisir —</option>{sups.map((s) => <option key={s.id} value={s.id}>{s.name}{s.orders_email || s.email ? "" : " (sans e-mail)"}</option>)}</select></F>
      {sups.length === 0 && <p className="text-xs text-amber-700">Aucun fournisseur : ajoutez-en un dans l'onglet Fournisseurs.</p>}
      <F label="Adresse de livraison (vide = adresse de la société)"><input className={inp} value={v.delivery_address} onChange={(e) => setV({ ...v, delivery_address: e.target.value })} /></F>
      <F label="Observations"><textarea rows={2} className={inp} value={v.notes} onChange={(e) => setV({ ...v, notes: e.target.value })} /></F>
      <div className="flex justify-end gap-2"><button className={btnGhost} onClick={onClose}>Annuler</button><button className={btnPrimary} disabled={busy || !v.supplier_id} onClick={go} data-testid="order-create-save">Créer le bon</button></div>
    </Modal>
  );
};

export const InvoiceDialog = ({ order, onClose, onDone }) => {
  const inv = order.invoice || {};
  const [v, setV] = useState({ number: inv.number || "", amount_ttc: inv.amount_ttc ?? order.total_ttc, invoice_date: inv.date || today() });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); Object.entries(v).forEach(([k, x]) => fd.append(k, String(x ?? "")));
      await http.post(`/equipment/orders/${order.id}/invoice`, fd); toast.success("Facture jointe au bon de commande"); onDone(); onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title={`Facture fournisseur — ${order.number}`} onClose={onClose} testid="invoice-dialog">
      {order.invoice && <p className="text-xs text-amber-700">Une facture est déjà jointe ({order.invoice.filename}) : elle sera remplacée.</p>}
      <F label="Fichier (PDF, JPG ou PNG · 10 Mo max.)"><input type="file" accept="application/pdf,image/jpeg,image/png" className={inp} onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="invoice-file" /></F>
      <div className="grid grid-cols-3 gap-3">
        <F label="N° de facture"><input className={inp} value={v.number} onChange={(e) => setV({ ...v, number: e.target.value })} data-testid="invoice-number" /></F>
        <F label="Date"><input type="date" className={inp} value={v.invoice_date} onChange={(e) => setV({ ...v, invoice_date: e.target.value })} /></F>
        <F label="Montant TTC"><input type="number" step="0.01" min={0} className={inp} value={v.amount_ttc} onChange={(e) => setV({ ...v, amount_ttc: e.target.value })} data-testid="invoice-amount" /></F>
      </div>
      {Number(v.amount_ttc) > 0 && Math.abs(Number(v.amount_ttc) - order.total_ttc) > 0.01 && <p className="text-xs text-amber-700" data-testid="invoice-gap">Écart avec le bon : {eur(Number(v.amount_ttc) - order.total_ttc)} (bon : {eur(order.total_ttc)} TTC)</p>}
      <div className="flex justify-end gap-2"><button className={btnGhost} onClick={onClose}>Annuler</button><button className={btnPrimary} disabled={busy || !file} onClick={go} data-testid="invoice-save">{busy ? "Envoi…" : "Joindre la facture"}</button></div>
    </Modal>
  );
};

export const SendDialog = ({ order, onClose, onDone }) => {
  const [to, setTo] = useState(order.supplier.orders_email || order.supplier.email || "");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await http.post(`/equipment/orders/${order.id}/send`, { to: to || null, message: msg }); toast.success(`Bon envoyé à ${to}`); onDone(); onClose(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title={`Envoyer le bon ${order.number}`} onClose={onClose} testid="order-send">
      <F label="E-mail du fournisseur"><input type="email" className={inp} value={to} onChange={(e) => setTo(e.target.value)} data-testid="order-send-to" /></F>
      <F label="Message (facultatif)"><textarea rows={4} className={inp} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Message par défaut avec le bon en pièce jointe (PDF)" /></F>
      <div className="flex justify-end gap-2"><button className={btnGhost} onClick={onClose}>Annuler</button><button className={btnPrimary} disabled={busy || !to} onClick={go} data-testid="order-send-save">{busy ? "Envoi…" : "Envoyer avec le PDF"}</button></div>
    </Modal>
  );
};

export const ReceiveDialog = ({ order, isValidator, onClose, onDone }) => {
  const [date, setDate] = useState(today());
  const [missing, setMissing] = useState([]);
  const [blocked, setBlocked] = useState(null);
  const [busy, setBusy] = useState(false);
  const lines = order.lines.filter((l) => !l.removed);
  const go = async (force = false) => {
    setBusy(true);
    try { await http.post(`/equipment/orders/${order.id}/receive`, { received_at: date, missing_line_ids: missing, force }); toast.success("Réception enregistrée : équipements ajoutés aux agents"); onDone(); onClose(); }
    catch (e) { const d = e?.response?.data?.detail; if (d?.code === "reorder_blocked") setBlocked(d.message); else toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Modal wide title={`Réception du bon ${order.number}`} onClose={onClose} testid="order-receive">
      <F label="Date de réception"><input type="date" className={inp} value={date} onChange={(e) => setDate(e.target.value)} /></F>
      <p className="text-sm text-slate-600">Décochez les articles que le fournisseur n'a pas livrés : ils seront retirés du bon et ne seront pas ajoutés à l'agent.</p>
      <div className="border border-slate-200 rounded-md divide-y">
        {lines.map((l) => (
          <label key={l.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`receive-line-${l.id}`}>
            <input type="checkbox" checked={!missing.includes(l.id)} onChange={(e) => setMissing(e.target.checked ? missing.filter((x) => x !== l.id) : [...missing, l.id])} />
            <span className="flex-1">{l.quantity} × <b>{l.name}</b>{l.size ? ` (${l.size})` : ""} — {l.agent_name}</span><span>{eur(l.quantity * l.unit_price_ht)}</span>
          </label>
        ))}
      </div>
      {blocked && <div className="text-sm bg-rose-50 border border-rose-200 text-rose-700 rounded-md px-3 py-2">{blocked}</div>}
      <div className="flex justify-end gap-2">
        <button className={btnGhost} onClick={onClose}>Annuler</button>
        {blocked && isValidator && <button className={`${btnGhost} text-rose-700`} onClick={() => go(true)} data-testid="receive-force">Forcer (responsable)</button>}
        <button className={btnPrimary} disabled={busy || !date || missing.length === lines.length} onClick={() => go(false)} data-testid="receive-save">Valider la réception</button>
      </div>
    </Modal>
  );
};
